import { FC, useState, useRef, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiChevronUp, FiCheck, FiLoader, FiX, FiZap, FiAlertTriangle, FiUserPlus, FiUsers } from 'react-icons/fi';
import { DashboardStatus } from '../../types/memberStatus';

// VeraScore range buckets
export type VeraScoreRange = 'all' | 'excellent' | 'good' | 'fair' | 'poor';

export const VERASCORE_RANGES: { value: VeraScoreRange; label: string; min: number; max: number; color: string }[] = [
  { value: 'excellent', label: 'Excellent (80-100)', min: 80, max: 100, color: 'bg-green-500' },
  { value: 'good', label: 'Good (60-79)', min: 60, max: 79, color: 'bg-blue-500' },
  { value: 'fair', label: 'Fair (40-59)', min: 40, max: 59, color: 'bg-yellow-500' },
  { value: 'poor', label: 'Needs Work (0-39)', min: 0, max: 39, color: 'bg-red-500' },
];

// Quick segment presets
export type QuickSegment = 'none' | 'loan_ready' | 'needs_attention' | 'new_members';

export interface QuickSegmentConfig {
  value: QuickSegment;
  label: string;
  icon: React.ReactNode;
  description: string;
  filters: Partial<SideFilterPanelProps['filters']>;
}

// Reuse the same props interface as TopFilterBar for consistency
interface SideFilterPanelProps {
  filters: {
    search: string;
    status: string[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    showLcuOnly: boolean;
    veraScoreRange: VeraScoreRange[];
    hasDashboardData: boolean | null; // null = all, true = has data, false = no data
    quickSegment: QuickSegment;
  };
  searchInput: string;
  onFilterChange: (filters: SideFilterPanelProps['filters']) => void;
  onSearchInputChange: (value: string) => void;
  onSearchSubmit: () => void;
  totalCount: number;
  filteredCount: number;
  isOpen: boolean;
  isSearching?: boolean;
  onClose?: () => void;
}

const SideFilterPanel: FC<SideFilterPanelProps> = ({
  filters,
  searchInput,
  onFilterChange,
  onSearchInputChange,
  onSearchSubmit,
  totalCount,
  filteredCount,
  isOpen,
  isSearching = false,
  onClose,
}) => {
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const statusOptions = Object.values(DashboardStatus);
  const sortOptions = [
    { value: 'member_name', label: 'Member Name' },
    { value: 'email', label: 'Email' },
    { value: 'verascore', label: 'VeraScore' },
    { value: 'dashboard_status', label: 'Status' },
    { value: 'last_profile_edit', label: 'Last Updated' },
  ];

  const allStatusesSelected = statusOptions.length === filters.status.length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleStatus = (status: string) => {
    const newStatuses = filters.status.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...filters.status, status];
    onFilterChange({ ...filters, status: newStatuses });
  };

  const toggleAllStatuses = () => {
    onFilterChange({
      ...filters,
      status: allStatusesSelected ? [] : [...statusOptions],
    });
  };

  // Handle search input with typing indicator
  const handleSearchChange = (value: string) => {
    // Update the input value immediately for UI responsiveness
    onSearchInputChange(value);

    // Debounce the actual search operation
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="h-full overflow-y-auto text-sm" style={{
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRight: '1px solid rgba(255, 255, 255, 0.5)',
      boxShadow: '0 4px 20px rgba(31, 38, 135, 0.07)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative glassmorphic elements */}
      <div
        style={{
          position: 'absolute',
          top: '-60px',
          right: '-60px',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)',
          filter: 'blur(20px)',
          zIndex: 0,
        }}
      ></div>
      <div
        style={{
          position: 'absolute',
          bottom: '-40px',
          left: '-40px',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 70%)',
          filter: 'blur(20px)',
          zIndex: 0,
        }}
      ></div>
      
      <div className="p-3 pb-6 flex flex-col gap-4 relative z-10">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-slate-800">Filters</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-white/50 transition-colors"
              aria-label="Close filters"
              style={{
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
            >
              <FiX size={18} className="text-slate-600" />
            </button>
          )}
        </div>

        {/* Results Count with Clear All button */}
        <div className="flex items-center justify-between mb-1 gap-2">
          <div className="text-xs text-slate-600 bg-white/40 px-2 py-1 rounded-md flex-grow" style={{
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            Showing {filteredCount} of {totalCount} members
          </div>

          {(filters.search !== '' || filters.status.length > 0 || filters.sortBy !== 'verascore' ||
            filters.sortOrder !== 'desc' || !filters.showLcuOnly ||
            filters.veraScoreRange.length > 0 || filters.hasDashboardData !== null ||
            filters.quickSegment !== 'none') && (
            <button
              onClick={() => {
                onSearchInputChange('');
                onFilterChange({
                  search: '',
                  status: [],
                  sortBy: 'verascore',
                  sortOrder: 'desc',
                  showLcuOnly: true,
                  veraScoreRange: [],
                  hasDashboardData: null,
                  quickSegment: 'none'
                });
                onSearchSubmit();
              }}
              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded flex items-center"
              title="Clear all filters"
            >
              <FiX size={12} className="mr-1" />
              Clear
            </button>
          )}
        </div>

        {/* Search Input */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700">Search</label>
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md focus:ring-brand-blue focus:border-brand-blue"
              placeholder="Search members..."
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
              }}
            />
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
              {isSearching ? (
                <FiLoader className="animate-spin" size={14} />
              ) : (
                <FiSearch size={14} />
              )}
            </div>
            {searchInput && (
              <button
                onClick={() => {
                  onSearchInputChange('');
                  onSearchSubmit(); // Automatically trigger search with empty string
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <FiX size={14} />
              </button>
            )}
          </div>
          <button
            onClick={onSearchSubmit}
            className="w-full mt-1 px-3 py-1.5 text-white rounded text-xs transition-all duration-200 hover:translate-y-[-1px]"
            style={{
              background: 'linear-gradient(90deg, #1C4583 0%, #3b82f6 100%)',
              border: '1px solid rgba(59, 130, 246, 0.5)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            Search
          </button>
        </div>

        {/* Status Filter */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700">Status</label>
          <div ref={statusDropdownRef} className="relative">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-sm"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
              }}
            >
              <span className="text-slate-700 truncate">
                {filters.status.length === 0
                  ? 'All Statuses'
                  : filters.status.length === 1
                    ? filters.status[0]
                    : `${filters.status.length} selected`}
              </span>
              {isStatusOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </button>

            {isStatusOpen && (
              <div className="absolute z-10 w-full mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
              }}>
                <div className="p-1.5 border-b border-slate-200/70">
                  <button
                    onClick={toggleAllStatuses}
                    className="flex items-center w-full px-2 py-1 hover:bg-slate-50/80 rounded text-xs"
                  >
                    <span className="w-5">
                      {allStatusesSelected && <FiCheck size={14} className="text-brand-blue" />}
                    </span>
                    <span>All Statuses</span>
                  </button>
                </div>
                <div className="p-1.5">
                  {statusOptions.map(status => (
                    <button
                      key={status}
                      onClick={() => toggleStatus(status)}
                      className="flex items-center w-full px-2 py-1 hover:bg-slate-50/80 rounded text-xs"
                    >
                      <span className="w-5">
                        {filters.status.includes(status) && (
                          <FiCheck size={14} className="text-brand-blue" />
                        )}
                      </span>
                      <span>{status}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sort By */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700">Sort By</label>
          <div ref={sortDropdownRef} className="relative">
            <button
              onClick={() => setIsSortOpen(!isSortOpen)}
              className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-sm"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
              }}
            >
              <span className="text-slate-700 truncate">
                {sortOptions.find(opt => opt.value === filters.sortBy)?.label || 'Member Name'}
              </span>
              {isSortOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </button>

            {isSortOpen && (
              <div className="absolute z-10 w-full mt-1 rounded-md shadow-lg" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
              }}>
                <div className="p-1.5">
                  {sortOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        onFilterChange({ ...filters, sortBy: option.value });
                        setIsSortOpen(false);
                      }}
                      className="flex items-center w-full px-2 py-1 hover:bg-slate-50/80 rounded text-xs"
                    >
                      <span className="w-5">
                        {filters.sortBy === option.value && (
                          <FiCheck size={14} className="text-brand-blue" />
                        )}
                      </span>
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sort Order */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700">Sort Order</label>
          <div className="flex gap-1">
            <button
              onClick={() => onFilterChange({ ...filters, sortOrder: 'asc' })}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs transition-all duration-200`}
              style={{
                backgroundColor: filters.sortOrder === 'asc' 
                  ? 'rgba(59, 130, 246, 0.9)' 
                  : 'rgba(255, 255, 255, 0.8)',
                color: filters.sortOrder === 'asc' ? 'white' : 'rgb(71, 85, 105)',
                border: filters.sortOrder === 'asc' 
                  ? '1px solid rgba(59, 130, 246, 0.7)' 
                  : '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: filters.sortOrder === 'asc'
                  ? '0 2px 5px rgba(59, 130, 246, 0.2)'
                  : '0 2px 5px rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
            >
              Ascending
            </button>
            <button
              onClick={() => onFilterChange({ ...filters, sortOrder: 'desc' })}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs transition-all duration-200`}
              style={{
                backgroundColor: filters.sortOrder === 'desc' 
                  ? 'rgba(59, 130, 246, 0.9)' 
                  : 'rgba(255, 255, 255, 0.8)',
                color: filters.sortOrder === 'desc' ? 'white' : 'rgb(71, 85, 105)',
                border: filters.sortOrder === 'desc' 
                  ? '1px solid rgba(59, 130, 246, 0.7)' 
                  : '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: filters.sortOrder === 'desc'
                  ? '0 2px 5px rgba(59, 130, 246, 0.2)'
                  : '0 2px 5px rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
            >
              Descending
            </button>
          </div>
        </div>

        {/* LCU Only Toggle */}
        <div className="space-y-1">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filters.showLcuOnly}
              onChange={() => onFilterChange({ ...filters, showLcuOnly: !filters.showLcuOnly })}
              className="w-3.5 h-3.5 text-brand-blue rounded border-slate-300 focus:ring-brand-blue"
            />
            <span className="text-xs font-medium text-slate-700">Show LCU Members Only</span>
          </label>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200/50 my-2"></div>

        {/* Quick Segments */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">Quick Segments</label>
          <div className="grid grid-cols-1 gap-1.5">
            {[
              { value: 'none' as QuickSegment, label: 'All Members', icon: <FiUsers size={14} />, description: 'Show all members' },
              { value: 'loan_ready' as QuickSegment, label: 'Loan Ready', icon: <FiZap size={14} />, description: 'High score, complete data' },
              { value: 'needs_attention' as QuickSegment, label: 'Needs Attention', icon: <FiAlertTriangle size={14} />, description: 'Low score or incomplete' },
              { value: 'new_members' as QuickSegment, label: 'New Members', icon: <FiUserPlus size={14} />, description: 'Recently joined' },
            ].map(segment => (
              <button
                key={segment.value}
                onClick={() => onFilterChange({ ...filters, quickSegment: segment.value })}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-all duration-200 text-left`}
                style={{
                  backgroundColor: filters.quickSegment === segment.value
                    ? 'rgba(59, 130, 246, 0.9)'
                    : 'rgba(255, 255, 255, 0.8)',
                  color: filters.quickSegment === segment.value ? 'white' : 'rgb(71, 85, 105)',
                  border: filters.quickSegment === segment.value
                    ? '1px solid rgba(59, 130, 246, 0.7)'
                    : '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: filters.quickSegment === segment.value
                    ? '0 2px 5px rgba(59, 130, 246, 0.2)'
                    : '0 2px 5px rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)'
                }}
                title={segment.description}
              >
                {segment.icon}
                <span>{segment.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* VeraScore Range Filter */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">VeraScore Range</label>
          <div className="space-y-1.5">
            {VERASCORE_RANGES.map(range => {
              const isSelected = filters.veraScoreRange.includes(range.value);
              return (
                <button
                  key={range.value}
                  onClick={() => {
                    const newRanges = isSelected
                      ? filters.veraScoreRange.filter(r => r !== range.value)
                      : [...filters.veraScoreRange, range.value];
                    onFilterChange({ ...filters, veraScoreRange: newRanges });
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-xs transition-all duration-200`}
                  style={{
                    backgroundColor: isSelected
                      ? 'rgba(59, 130, 246, 0.15)'
                      : 'rgba(255, 255, 255, 0.8)',
                    color: 'rgb(71, 85, 105)',
                    border: isSelected
                      ? '1px solid rgba(59, 130, 246, 0.5)'
                      : '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)'
                  }}
                >
                  <span className={`w-3 h-3 rounded-full ${range.color}`}></span>
                  <span className="flex-1 text-left">{range.label}</span>
                  {isSelected && <FiCheck size={14} className="text-brand-blue" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Has Dashboard Data Filter */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">Dashboard Data</label>
          <div className="flex gap-1">
            {[
              { value: null, label: 'All' },
              { value: true, label: 'Has Data' },
              { value: false, label: 'No Data' },
            ].map(option => (
              <button
                key={String(option.value)}
                onClick={() => onFilterChange({ ...filters, hasDashboardData: option.value })}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs transition-all duration-200`}
                style={{
                  backgroundColor: filters.hasDashboardData === option.value
                    ? 'rgba(59, 130, 246, 0.9)'
                    : 'rgba(255, 255, 255, 0.8)',
                  color: filters.hasDashboardData === option.value ? 'white' : 'rgb(71, 85, 105)',
                  border: filters.hasDashboardData === option.value
                    ? '1px solid rgba(59, 130, 246, 0.7)'
                    : '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: filters.hasDashboardData === option.value
                    ? '0 2px 5px rgba(59, 130, 246, 0.2)'
                    : '0 2px 5px rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideFilterPanel;
